import type { ICommentRepository } from "@/application/ports/repositories";
import type {
  CommentListParams,
  CommentPerson,
  CommentSubjectScope,
  CommentSummary,
  CommentThreadDetail,
  CommentThreadDraft,
  CommentThreadList,
} from "@/domain/comment";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";
export class CommentRepository implements ICommentRepository {
  async list(params: CommentListParams = {}, signal?: AbortSignal): Promise<CommentThreadList> {
    const { items, ...page } = await unwrap(sdk.listCommentThreads({ query: params, signal }));
    return { threads: items, ...page };
  }
  async get(uuid: string, signal?: AbortSignal): Promise<CommentThreadDetail> {
    return unwrap(sdk.getCommentThread({ path: { thread_uuid: uuid }, signal }));
  }
  async summary(
    { subject_type, subject_uuid }: CommentSubjectScope = {},
    signal?: AbortSignal,
  ): Promise<CommentSummary> {
    return unwrap(sdk.commentSummary({ query: { subject_type, subject_uuid }, signal }));
  }
  async mentionable(signal?: AbortSignal): Promise<CommentPerson[]> {
    const response = await unwrap(sdk.mentionableUsers({ signal }));
    return response.items;
  }
  async create(draft: CommentThreadDraft): Promise<CommentThreadDetail> {
    return unwrap(sdk.createCommentThread({ body: draft }));
  }
  async reply(uuid: string, body: string): Promise<CommentThreadDetail> {
    return unwrap(sdk.replyToCommentThread({ path: { thread_uuid: uuid }, body: { body } }));
  }
  async editMessage(uuid: string, body: string): Promise<void> {
    await unwrap(sdk.editCommentMessage({ path: { message_uuid: uuid }, body: { body } }));
  }
  async deleteMessage(uuid: string): Promise<void> {
    await unwrap(sdk.deleteCommentMessage({ path: { message_uuid: uuid } }));
  }
  async deleteThread(uuid: string): Promise<void> {
    await unwrap(sdk.deleteCommentThread({ path: { thread_uuid: uuid } }));
  }
  async resolve(uuid: string): Promise<void> {
    await unwrap(sdk.resolveCommentThread({ path: { thread_uuid: uuid } }));
  }
  async reopen(uuid: string): Promise<void> {
    await unwrap(sdk.reopenCommentThread({ path: { thread_uuid: uuid } }));
  }
  async mute(uuid: string): Promise<void> {
    await unwrap(sdk.muteCommentThread({ path: { thread_uuid: uuid } }));
  }
  async unmute(uuid: string): Promise<void> {
    await unwrap(sdk.unmuteCommentThread({ path: { thread_uuid: uuid } }));
  }
  async react(uuid: string, emoji: string): Promise<void> {
    await unwrap(sdk.reactToCommentMessage({ path: { message_uuid: uuid }, body: { emoji } }));
  }
  async removeReaction(uuid: string, emoji: string): Promise<void> {
    await unwrap(sdk.removeCommentReaction({ path: { message_uuid: uuid, emoji_code: emoji } }));
  }
}
